import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { ChevronRight, Check } from 'lucide-react';

const SlideToConfirm = ({ onConfirm, label, color = 'blue', t }) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const x = useMotionValue(0);
  const containerRef = useRef(null);

  // Compact dimensions
  const trackWidth = 140; 
  const handleWidth = 28; // w-7
  const maxDrag = trackWidth - handleWidth - 8; // padding/margin adjustment

  const textOpacity = useTransform(x, [0, trackWidth / 2], [1, 0]);
  const iconRotate = useTransform(x, [0, maxDrag], [0, 360]);
  
  // Specific color logic for Tailwind classes
  const colorMap = {
    urgent: '#b91c1c', // Red-700
    high: '#b45309',   // Amber-700
    medium: '#4338ca', // Indigo-700
    low: '#059669',    // Emerald-600
    primary: '#2563eb'
  };
  
  const accentColor = colorMap[color] || colorMap.medium || '#4338ca';

  const handleDragEnd = () => {
    if (x.get() > (maxDrag * 0.85)) {
      setIsConfirmed(true);
      x.set(maxDrag);
      if (onConfirm) onConfirm();
    } else {
      x.set(0);
    }
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full max-w-[160px] h-9 bg-white/5 border border-white/10 rounded-xl overflow-hidden cursor-pointer group select-none transition-all hover:bg-white/[0.08]"
    >
      {/* Track Background */}
      <motion.div 
        className="absolute inset-0 opacity-10"
        style={{ backgroundColor: accentColor }}
      />

      {/* Label Text */}
      <motion.div 
        style={{ opacity: textOpacity }}
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
      >
        <span className="text-[9px] font-black uppercase tracking-[0.1em] text-slate-500 group-hover:text-slate-400 transition-colors px-4 text-center leading-tight">
          {isConfirmed ? (t ? t('confirmed') : 'Confirmed') : label || (t ? t('slide') : 'Slide')}
        </span>
      </motion.div>

      {/* Handle */}
      {!isConfirmed && (
        <motion.div
          drag="x"
          dragConstraints={{ left: 0, right: maxDrag }}
          dragElastic={0.05}
          onDragEnd={handleDragEnd}
          style={{ x, backgroundColor: accentColor }}
          className="absolute left-1 top-1 bottom-1 w-7 shadow-lg rounded-lg flex items-center justify-center text-white cursor-grab active:cursor-grabbing z-10 transition-shadow"
        >
          <motion.div style={{ rotate: iconRotate }}>
             <ChevronRight size={14} strokeWidth={3} />
          </motion.div>
        </motion.div>
      )}

      {/* Confirmation State */}
      {isConfirmed && (
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: `${accentColor}22`, color: accentColor }}
        >
          <Check size={14} strokeWidth={3} className="mr-1.5" />
          <span className="text-[9px] font-black uppercase tracking-widest">{t ? t('ok') : 'OK'}</span>
        </motion.div>
      )}
    </div>
  );
};

export default SlideToConfirm;
